import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { confirmRefrenceCondition } from "../../network/cipAPI";
import DupontButton from "../../components/DupontButton";
import DupontRadio from "../../components/DupontRadio";
import InputBox from "../../components/InputBox";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import { OverlayLoader } from "../../components/PageLoading";
import { TextFieldValidator } from "../../components/InputBox/TextFieldValidator";
import PopoverText from "../../components/PopoverText";
import InfoIconGreen from "../../assets/info-icon-green.svg";
import { UpdateSetWithKey } from "../../utils/Utils";
import { REFERENCE_CONDITION_OPTIONS } from "./columns.config";

import "./index.scss";

const BASE_INPUT_LIMITS = {
  feed_flowrate: { min: 0, max: 1000 },
  feed_temp: { min: 0, max: 40 },
  feed_pressure: { min: 0, max: 6 },
  filtrate_flowrate: { min: 0, max: 1000 },
  filtrate_pressure: { min: 0, max: 6 },
  tmp_drop: { min: 0, max: 3 },
  feed_suspended: { min: 0, max: 300 },
  filtrate_suspended: { min: 0, max: 3 },
};

const BAR_TO_PSIG = 14.5038;

const FLOW_CONVERSION_FACTORS = {
  "m³/h_m³/d": 24,
  "m³/h_gpm": 4.40287,
  "m³/d_m³/h": 0.04166,
  "m³/d_gpm": 0.18345,
  "gpm_m³/h": 0.22712,
  "gpm_m³/d": 5.45107,
};

const convertTemperature = (value, fromUnit, toUnit) =>
  fromUnit === toUnit
    ? value
    : fromUnit === "C"
    ? (value * 9) / 5 + 32
    : ((value - 32) * 5) / 9;

const convertPressure = (value, fromUnit, toUnit) =>
  fromUnit === toUnit
    ? value
    : fromUnit === "bar"
    ? value * BAR_TO_PSIG
    : value / BAR_TO_PSIG;

const convertFlow = (value, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return value;
  const conversionKey = `${fromUnit}_${toUnit}`;
  return FLOW_CONVERSION_FACTORS[conversionKey]
    ? value * FLOW_CONVERSION_FACTORS[conversionKey]
    : value;
};

const MANDATORY_FIELDS = [
  "feed_temp",
  "feed_flowrate",
  "filtrate_flowrate",
  "feed_pressure",
  "filtrate_pressure",
  "tmp_drop",
];

const INITIAL_ERRORS = {
  feed_flowrate: null,
  feed_temp: null,
  feed_pressure: null,
  filtrate_flowrate: null,
  filtrate_pressure: null,
  tmp_drop: null,
  feed_suspended: null,
  filtrate_suspended: null,
};

const formatReference = (reference) => {
  const formatted = { ...reference };
  for (const key in formatted) {
    const val = formatted[key];
    if (typeof val === "number") {
      formatted[key] = val === 0 ? "0" : Number(val.toFixed(2)).toString();
    }
  }
  return formatted;
};

const parsePayload = (data) => {
  const parsed = {};
  for (const key in data) {
    if (key === "date" || key === "plant_id") continue; // Exclude 'date' and 'plant_id'
    const value = data[key];
    if (value && !isNaN(parseFloat(value)) && value.trim() !== "") {
      parsed[key] = parseFloat(value);
    } else if (value) {
      parsed[key] = value;
    }
  }
  return parsed;
};

const ReferenceCondition = ({
  fileId,
  mappedReference,
  isEdit,
  setisReferenceEnabled,
  selectedColumnRadioColumn,
  setIsProceedEnabled,
  selectedCheckColumn,
  setView,
  setHasReferenceError,
}) => {
  const { t } = useTranslation();

  const referenceColumns = useMemo(
    () =>
      REFERENCE_CONDITION_OPTIONS(
        t,
        selectedColumnRadioColumn,
        selectedCheckColumn
      ),
    [t, selectedColumnRadioColumn, selectedCheckColumn]
  );

  const { flow, pressure, feed_suspended, filtrate_suspended, temperature } =
    useSelector((state) => state);
  const [defaultSettingUnit] = useState({
    flow,
    pressure,
    feed_suspended,
    filtrate_suspended,
    temperature,
  });

  const dynamicInputLimits = useMemo(() => {
    const limits = { ...BASE_INPUT_LIMITS };

    if (temperature !== "C") {
      const { min, max } = BASE_INPUT_LIMITS.feed_temp;
      limits.feed_temp = {
        min: Math.round(convertTemperature(min, "C", temperature) * 100) / 100,
        max: Math.round(convertTemperature(max, "C", temperature) * 100) / 100,
      };
    }

    if (pressure !== "bar") {
      ["feed_pressure", "filtrate_pressure", "tmp_drop"].forEach((field) => {
        const { min, max } = BASE_INPUT_LIMITS[field];
        limits[field] = {
          min: Math.round(convertPressure(min, "bar", pressure) * 100) / 100,
          max: Math.round(convertPressure(max, "bar", pressure) * 100) / 100,
        };
      });
    }

    if (flow !== "m³/h") {
      ["feed_flowrate", "filtrate_flowrate"].forEach((field) => {
        const { min, max } = BASE_INPUT_LIMITS[field];
        limits[field] = {
          min: Math.round(convertFlow(min, "m³/h", flow) * 100) / 100,
          max: Math.round(convertFlow(max, "m³/h", flow) * 100) / 100,
        };
      });
    }

    return limits;
  }, [temperature, pressure, flow]);

  const [selectedRadioColumn, setSelectedRadioColumn] = useState({
    column19: "pressure_drop",
  });
  const [selectedReference, setSelectedReference] = useState({});
  const [changedSelectedReference, setChangedSelectedReference] = useState({});
  const [showOverLayLoader, setShowOverLayLoader] = useState(false);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [isSaved, setIsSaved] = useState(false);

  // Track previous mappedReference for comparison and first load per fileId
  const prevMappedReference = React.useRef();
  const firstLoad = React.useRef(true);
  // Expose firstLoad ref globally for cross-component access (hack for button enable logic)
  if (typeof window !== "undefined") {
    window.firstLoadRefForReferenceCondition = firstLoad;
  }
  useEffect(() => {
    if (mappedReference) {
      const formatted = formatReference(mappedReference);
      setSelectedReference(formatted);
      if (isEdit) {
        if (firstLoad.current) {
          setChangedSelectedReference({}); // Initial load: keep disabled
        } else if (
          prevMappedReference.current &&
          JSON.stringify(prevMappedReference.current) !==
            JSON.stringify(formatted)
        ) {
          setChangedSelectedReference({ ...formatted }); // Enable if changed after initial load
        }
      } else {
        setChangedSelectedReference({ ...formatted });
      }
      prevMappedReference.current = formatted;
      if (isEdit) setisReferenceEnabled(false);
      setErrors(INITIAL_ERRORS);
      setIsSaved(false);

      const newErrors = { ...INITIAL_ERRORS };
      Object.keys(formatted).forEach((key) => {
        const value = formatted[key];
        if (value && !isNaN(parseFloat(value)) && value.trim() !== "") {
          const numValue = parseFloat(value);
          const { min: minValue, max: maxValue } =
            dynamicInputLimits[key] || {};

          if (minValue !== undefined && maxValue !== undefined) {
            const getUnitForField = (fieldKey) => {
              const unitMap = {
                feed_temp: "temperature",
                feed_pressure: "pressure",
                filtrate_pressure: "pressure",
                tmp_drop: "pressure",
                feed_flowrate: "flow",
                filtrate_flowrate: "flow",
                feed_suspended: "feed_suspended",
                filtrate_suspended: "filtrate_suspended",
              };
              return defaultSettingUnit[unitMap[fieldKey]] || "";
            };

            const unit = getUnitForField(key);
            const unitText = unit ? ` ${unit}` : "";

            if ((value === "" && minValue !== null) || parseFloat(value) <= 0) {
              newErrors[key] = `${t(
                "defaultInformationStrings.greaterThanMin"
              )} ${minValue}`;
            } else if (
              maxValue !== null &&
              (parseFloat(value) < minValue || parseFloat(value) > maxValue)
            ) {
              newErrors[key] = `${t(
                "defaultInformationStrings.greaterThanMin"
              )} ${minValue} ${t(
                "defaultInformationStrings.lessThanEqualto"
              )} ${maxValue}`;
            }
          }
        }
      });
      setErrors(newErrors);
      firstLoad.current = false;
    }
  }, [mappedReference, dynamicInputLimits, t, defaultSettingUnit, isEdit]);

  useEffect(() => {
    if (!fileId) {
      setSelectedReference({});
      setChangedSelectedReference({});
      setErrors(INITIAL_ERRORS);
      setIsSaved(false);
    }
    firstLoad.current = true;
  }, [fileId]);

  const getKey = (column) =>
    column.options === undefined ? column.key : selectedRadioColumn[column.key];
  // const handleOptionChange = (event) => {
  //   setisReferenceEnabled(false);
  //   const { name, value } = event.target;
  //   setSelectedRadioColumn({ ...selectedRadioColumn, [name]: value });
  // };
  // const getchecked = (column, key) => {
  //   return selectedRadioColumn[column] === key;
  // };

  const onInputValChange = (value, key) => {
    setisReferenceEnabled(false);
    setIsProceedEnabled(true);
    setIsSaved(false);

    setSelectedReference((prev) => {
      const newState = { ...prev, [key]: value };
      return newState;
    });

    setChangedSelectedReference((prev) => {
      const newSet = UpdateSetWithKey(prev, key, value);
      return newSet;
    });

    let error = null;
    if (value && !isNaN(parseFloat(value)) && value.trim() !== "") {
      const numValue = parseFloat(value);
      const { min: minValue, max: maxValue } = dynamicInputLimits[key] || {};
      if (minValue !== undefined && maxValue !== undefined) {
        const getUnitForField = (fieldKey) => {
          const unitMap = {
            feed_temp: "temperature",
            feed_pressure: "pressure",
            filtrate_pressure: "pressure",
            tmp_drop: "pressure",
            feed_flowrate: "flow",
            filtrate_flowrate: "flow",
            feed_suspended: "feed_suspended",
            filtrate_suspended: "filtrate_suspended",
          };
          return defaultSettingUnit[unitMap[fieldKey]] || "";
        };

        const unit = getUnitForField(key);
        const unitText = unit ? ` ${unit}` : "";

        if ((value === "" && minValue !== null) || parseFloat(value) <= 0) {
          error = `${t(
            "defaultInformationStrings.greaterThanMin"
          )} ${minValue}`;
        } else if (
          maxValue !== null &&
          (parseFloat(value) < minValue || parseFloat(value) > maxValue)
        ) {
          error = `${t(
            "defaultInformationStrings.greaterThanMin"
          )} ${minValue} ${t(
            "defaultInformationStrings.lessThanEqualto"
          )} ${maxValue}`;
        }
      }
    }

    setErrors((prev) => ({ ...prev, [key]: error }));
  };

  const getConfirmButtonState = () => {
    // Disable if any error exists
    const hasError = Object.values(errors).some((err) => !!err);
    if (hasError) return true;

    const {
      feed_temp,
      feed_pressure,
      filtrate_pressure,
      tmp_drop,
      filtrate_flowrate,
      feed_flowrate,
    } = selectedReference;

    const hasAllInputs =
      !!feed_temp &&
      (!!tmp_drop || (!!feed_pressure && !!filtrate_pressure)) &&
      (!!feed_flowrate || !!filtrate_flowrate);

    const noErrors = MANDATORY_FIELDS.every((field) => {
      const value = selectedReference[field];
      return (
        !value ||
        value.trim() === "" ||
        errors[field] === null ||
        errors[field] === ""
      );
    });

    if (isSaved) return true;

    // In edit mode, only enable if changes are made
    if (isEdit) {
      return !(
        fileId &&
        hasAllInputs &&
        noErrors &&
        Object.keys(changedSelectedReference).length > 0
      );
    }

    // In create mode, original logic
    return !(fileId && hasAllInputs && noErrors);
  };

  const confirmRefrenceConditions = async () => {
    setShowOverLayLoader(true);
    try {
      const payload = parsePayload(selectedReference);

      const response = await confirmRefrenceCondition(fileId, payload);
      if (response) {
        setIsProceedEnabled(false);
        setView(2);
        setChangedSelectedReference({});
        setIsSaved(true);
        if (response?.detail?.error) {
          createNotification(
            ERROR_NOTIFICATION,
            t("analyseData.pressureErrorMessage"),
            ""
          );
        } else {
          createNotification(
            SUCCESS_NOTIFICATION,
            t("analyseData.referenceAdded"),
            ""
          );
        }
      } else {
        createNotification(
          ERROR_NOTIFICATION,
          response?.detail?.error || t("analyseData.saveFailed"),
          ""
        );
      }
    } catch (err) {
      console.error("Save Error:", err);
      createNotification(
        ERROR_NOTIFICATION,
        t("analyseData.saveFailed"),
        err.message
      );
    } finally {
      setShowOverLayLoader(false);
    }
  };

  useEffect(() => {
    if (setHasReferenceError) {
      const hasError = Object.values(errors).some((err) => !!err);
      setHasReferenceError(hasError);
    }
  }, [errors, setHasReferenceError]);

  return (
    <div className="lightGrayBorder padding10 position-relative">
      {showOverLayLoader && <OverlayLoader customHeight="300px" />}
      <div className="box-title-info box-title text-align-left">
        <span>{t("analyseData.referenceCondition")}</span>
        <PopoverText
          descriptor={<img src={InfoIconGreen} alt="icon" />}
          descriptionText={t("analyseData.referenceConditionDescription")}
        />
      </div>
      <div className="flex-ai-center container-padding position-relative">
        <div className="grid-container-column">
          {Object.keys(referenceColumns).map((referenceColumn, index) => {
            const column = referenceColumns[referenceColumn];
            if (column.hide) return null;
            const key = getKey(column);
            return (
              <div className="grid-item-column" key={index}>
                <div className="width-250">
                  <label>
                    {column.name?.replace(
                      "<unit>",
                      `(${defaultSettingUnit[column.unit]})`
                    )}
                    {column.isMandatory && (
                      <span style={{ color: "red" }}>*</span>
                    )}
                  </label>
                </div>
                <div className="width-dropdown">
                  <InputBox
                    name="reference"
                    key={column.key}
                    value={selectedReference[key] || ""}
                    onChange={(value) => onInputValChange(value, key)}
                    validator={TextFieldValidator.NUMBER_WITH_DECIMAL}
                    min={dynamicInputLimits[key]?.min}
                    max={dynamicInputLimits[key]?.max}
                    errorMsg={errors[key]}
                    maxLength={10}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="reference-button ">
          <DupontButton
            disabled={getConfirmButtonState()}
            customClass="margin-left-10 "
            onClick={confirmRefrenceConditions}
          >
            {t("analyseData.confirmReferenceSelection")}
          </DupontButton>
        </div>
      </div>
    </div>
  );
};

export default ReferenceCondition;
